import ErrorMessage from '@lib/components/ErrorMessage';
import Menu from '@lib/components/Menu';
import TitleBar, * as ITitleBar from '@lib/components/TitleBar';
import { DashBoardProps } from '@lib/containers/DashBoard';
import { IInputChannel, INativeNavigator, IOutputChannel, ITempChannel } from '@lib/types';
import * as React from 'react';
import { IMAGE_PATH, isTemperaturInRange } from 'util/util';
import './DashBoard.css';


interface IDashBoardState {
  showMenu: boolean;
}


/**
 * Class for Dashbord page
 */
class DashBoard extends React.Component<DashBoardProps, IDashBoardState> {

  private interval: number;

  public constructor(props: DashBoardProps) {
    super(props);
    this.state = { showMenu: false };
  }

  /**
   * Schedule auto-refresh of data
   */
  public componentDidMount() {
    if (this.props.site !== undefined && this.props.site.device !== undefined) {
      this.refreshState(5000);
    }
  }

  /**
   * When component will leave, cancel the interval
   */
  public componentWillUnmount() {
    window.clearInterval(this.interval);
  }

  /**
   * Fired when user clicks on the sync button
   * @param event 
   */
  public onSyncClick(event: React.MouseEvent<HTMLDivElement>) {
    if (this.props.site !== undefined && this.props.site.device !== undefined) {
      if (!this.props.syncing) {
        this.props.onSync(this.props.site.device!.id);
        this.refreshState(1000);
      } else {
        /* tslint:disable:no-empty */
        (navigator as INativeNavigator).notification.alert(this.props.i18n.DashBoard.waitForSync, () => { }, 'Smart TX');
      }
    }
  }

  /**
   * Fired when user clicks on an output to change its state
   * @param event 
   */
  public onOutputClick(id: number, value: number, event: React.MouseEvent<HTMLDivElement>) {
    if (value === 2) {
      return;
    }
    // Cancel interval, we will reschedule it later
    window.clearInterval(this.interval);
    // Call the API to change the state of the output
    this.props.onChangeOuput(this.props.site.device!.id, id, (value === 0 ? 1 : 0));
    // Check every seconds, pendingOrder will prevent effective call
    this.refreshState(1000);
  }

  /**
   * Fired when user clicks on a temperature pencil to edit it
   * @param no
   * @param event 
   */
  public onTemperatureEditClick(temp: ITempChannel, event: React.MouseEvent<HTMLDivElement>) {
    if (temp === undefined || temp.no === undefined || !isTemperaturInRange(temp.value)) {
      return;
    }
    this.props.onEditTemperature(temp.no);
  }


  /**
   * Fired when user clicks on a thermostat to edit it
   * @param no
   * @param event 
   */
  public onThermostatEditClick(temp: ITempChannel, event: React.MouseEvent<HTMLDivElement>) {
    if (temp === undefined || temp.no === undefined ||
      !isTemperaturInRange(temp.value)) {
      return;
    }
    this.props.onEditThermostat(temp.no);
  }


  /**
   * Fired when user clicks on an output pencil to edit it
   * @param no
   * @param event 
   */
  public onOutputEditClick(no: number, event: React.MouseEvent<HTMLDivElement>) {
    if (no === undefined) {
      return;
    }
    this.props.onEditOutput(no);
  }

  /**
   * Fired when user clicks on an input pencil to edit it
   * @param no
   * @param event 
   */
  public onInputEditClick(no: number, event: React.MouseEvent<HTMLDivElement>) {
    if (no === undefined) {
      return;
    }
    this.props.onEditInput(no);
  }



  /**
   * Fired when user click on menu hamburger button
   * @param event 
   */
  public onMenuClick(event: React.MouseEvent<HTMLDivElement>) {
    this.setState({ showMenu: !this.state.showMenu });
  }



  /**
   * Fired when user click on menu push item
   * @param event 
   */
  public onPushClick(state: any, s: any, event: React.MouseEvent<HTMLDivElement>) {
    // Call API
    event.stopPropagation();
    this.props.onPushClick(!this.props.push, this.props.token);
  }




  /**
   * Fired when user click on menu help item
   * @param event 
   */
  public onHelpClick(state: any, event: React.MouseEvent<HTMLDivElement>) {
    event.stopPropagation();
    window.open('https://static.smart-tx.io/docs/Documentation.pdf', '_system');
  }

  public render() {

    if (this.props.site === undefined ||
      this.props.site.device === undefined ||
      this.props.site.temps === undefined ||
      this.props.site.inputs === undefined ||
      this.props.site.outputs === undefined) {
      window.location.pathname = '/';
      window.location.hash = '';
      return null;
    };

    const { inputs, outputs, temps } = this.props.site;

    return (
      <div className="standardPage dashboard">
        <Menu i18n={{ i18n: this.props.i18n }} show={this.state.showMenu}
          onASite={true}
          push={this.props.push}
          mail={this.props.login}
          onCloseClick={this.onMenuClick.bind(this, this.state)}
          onHelpClick={this.onHelpClick.bind(this, this.state)}
          onPushClick={this.onPushClick.bind(this, this.state)}
          onSiteClick={this.props.onGoSiteList.bind(this, this.state)}
          onChangePasswordClick={this.props.onGoChangePassword.bind(this, this.state)}
          onSignoutClick={this.props.onGoHome.bind(this, this.state)}
          onViewAlarmList={this.props.onViewAlarmList.bind(this, this.props.site.device!.id)}
          onViewLogEventList={this.props.onViewLogEventList.bind(this, this.props.site.device!.id)}
          onViewUserList={this.props.onViewUserList.bind(this, this.props.site.device!.id)}
          token={this.props.token}
        />

        <div className="contentCentered">
          <TitleBar
            leftIcon={ITitleBar.ICO_BAR}
            onLeftButtonClick={this.onMenuClick.bind(this, this.state)}
            title={this.props.site.name}
            rightIcon={this.props.syncing ? ITitleBar.ICO_SYNC_ALT_ROTATING : ITitleBar.ICO_SYNC_ALT}
            onRightButtonClick={this.onSyncClick.bind(this, this.state)}
          />

          {
            !this.props.site.device.connected &&
            <div className="offline">
              {this.props.i18n.DashBoard.offLine}
            </div>
          }
          <ErrorMessage message={this.props.i18n.Common.errors[this.props.errorMessage]} />
          <div className='grid'>
            {/* Themostat */}
            <div className='rowTemp'>
              {temps.map((temp: ITempChannel) => (
                <div key={temp.no}>
                  <div onClick={this.onThermostatEditClick.bind(this, temp)} className="thermostat">
                    <div className="circle">
                      <div>
                        <span className={temp.inAlarm ? 'temp tempAlarm' : 'temp'}>{!isTemperaturInRange(temp.value) ? '---' : temp.value.toFixed(1) + '°C'}</span>
                      </div>
                      <div className={'thermostatMode' + temp.isInDerogation + '-' + temp.currentThermostatMode} >&nbsp;</div>
                    </div>
                  </div>

                  <div onClick={this.onTemperatureEditClick.bind(this, temp)} className="thermostatname">
                    <span>{isTemperaturInRange(temp.value) ? temp.label : this.props.i18n.DashBoard.noSensor}</span>
                    {(isTemperaturInRange(temp.value)) &&
                      <img src={IMAGE_PATH + 'color3_25_pencil-alt@3x.png'} />
                    }
                  </div>

                </div>
              ))}
            </div>
            {/* Alarms */}
            {inputs.map((input: IInputChannel) => (
              <div className="alarm" key={input.no}>
                <div className="alarmIcoAndName">
                  {input.mode.toString() !== '2' &&
                    <div className="warning">
                      <img src={input.value === 0 ? (IMAGE_PATH + 'color1_30_warning@3x.png') : (IMAGE_PATH + 'color9_30_warning@3x.png')} />
                    </div>
                  }
                  {input.mode.toString() === '2' &&
                    <div className="warning">
                      <img src={IMAGE_PATH + 'color1_25_sort-numeric-up@3x.png'} />
                    </div>
                  }
                  <div className="alarmName">
                    <span>{input.label}</span>
                    {input.mode.toString() === '2' &&
                      <span className={input.counterMaxReached ? 'counterMaxReached' : ''}>{' : ' + input.total + ' ' + input.counterUnit}</span>
                    }
                  </div>
                </div>
                <div onClick={this.onInputEditClick.bind(this, input.no)} className="alarmPen">
                  <img src={IMAGE_PATH + 'color3_25_pencil-alt@3x.png'} />
                </div>
              </div>
            ))}
            {/* Outputs */}
            {outputs.map((output: IOutputChannel) => (
              <div className="output" key={output.no}>
                <div className="outputIcoAndName" onClick={this.onOutputClick.bind(this, output.no, output.value)}>
                  <div className="warning">
                    <img src={output.value === 0 ? (IMAGE_PATH + 'power_off@3x.png') :
                      (output.value === 1 ? (IMAGE_PATH + 'power_on@3x.png') : (IMAGE_PATH + 'power_loading@3x.gif'))} />
                  </div>
                  <div className="outputName">
                    <span>{output.label}</span>
                  </div>
                </div>
                <div onClick={this.onOutputEditClick.bind(this, output.no)} className="outputPen">
                  <img src={IMAGE_PATH + 'color3_25_pencil-alt@3x.png'} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div >
    );
  }

  /**
   * Schedule refresh of the state of the device
   * @param timeout time in milli seconds between 2 refresh
   */
  private refreshState(timeout: number) {
    window.clearInterval(this.interval);
    this.interval = window.setInterval(() => {
      if (this.props.site !== undefined && this.props.site.device !== undefined) {
        if (this.props.pendingOrder <= 0 && !this.props.syncing) {
          this.refreshState(5000);
          // window.clearInterval(this.interval);
          this.props.onSelect(this.props.site.device!.id, this.props.site.name);
        }
      } else {
        window.location.hash = 'home';
      }
    }, timeout);
  }
}

export default DashBoard;



// WEBPACK FOOTER //
// ./src/pages/DashBoard.tsx